<!-- Profile picker (#profilePicker, z-90): full screen, always shown at start-up (the app gets ready underneath with the
     last profile used) and from "Switch profile" in the Profiles window. Profiles are circles with photo or initial; the
     "+" circle opens the profile card for a new profile (ProfileEditDialog.vue). Entrance animation (.pick-in in
     legacy.css): first the logo, title and subtitle, then the circles one after the other. Animated "aurora" background
     behind the content (src/features/aurora.ts), only while it is on screen. No global shortcut works while it is open
     (src/features/keyboard.ts). Open state: dialogs store 'picker'; choosing a profile: picker store pick(). -->
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import HideoLogo from './HideoLogo.vue'
import AppFooter from './AppFooter.vue'
import { useI18n } from '../composables/useI18n'
import { useDataStore } from '../stores/data'
import { usePickerStore } from '../stores/picker'
import { profileInitial } from '../features/profiles'
import { startAurora } from '../features/aurora'
import { bgImage } from '../utils/css'

const { t } = useI18n()
const store = useDataStore()
const picker = usePickerStore()
const size = 'w-24 h-24 md:w-32 md:h-32 text-3xl md:text-4xl'
const photoStyle = (avatar: string) => bgImage(avatar)
const delay = (ms: number) => `animation-delay: ${ms}ms;`
// Only what is on screen when the picker appears enters with the animation (its delay never changes afterwards)
const startPos = new Map(store.profiles.list.map((p, i) => [p.id, i]))
const enter = (id: string) => (startPos.has(id) ? delay(300 + startPos.get(id)! * 90) : undefined)

const canvas = ref<HTMLCanvasElement | null>(null)
let stop = () => {}
onMounted(() => { if (canvas.value) stop = startAurora(canvas.value) })
onBeforeUnmount(() => stop())
</script>

<template>
  <Teleport to="body">
    <div id="profilePicker" class="fixed inset-0 z-[90] bg-darkbg flex flex-col items-center px-6 pt-6 pb-2 overflow-y-auto">
      <!-- Animated "aurora" background: behind the content (z-index -1 inside the profile picker) -->
      <canvas id="pickerAurora" ref="canvas" aria-hidden="true" class="fixed inset-0 w-full h-full pointer-events-none" style="z-index:-1"></canvas>
      <!-- The content stays centred (m-auto); the footer sits at the bottom, below it when the list is long -->
      <div class="m-auto flex flex-col items-center w-full">
        <div class="text-xl font-black tracking-wider text-brand mb-8 flex flex-col items-center gap-3 pick-in" :style="delay(0)"><HideoLogo class="w-16 h-16" /><span class="flex flex-col items-center leading-tight"><span class="text-gray-100">HIDEO</span><span class="text-xs font-semibold tracking-normal text-gray-400 mt-1">{{ t('Your content plan, in a moment') }}</span></span></div>
        <h2 class="text-2xl md:text-4xl font-black text-gray-100 text-center pick-in" :style="delay(90)">{{ t("Who's using Hideo?") }}</h2>
        <p id="pickerSub" class="text-sm text-gray-400 mt-2 mb-10 text-center pick-in" :style="delay(180)">{{ store.profiles.list.length ? t('Choose your profile') : t('Create your first profile to get started') }}</p>
        <div id="pickerGrid" class="flex flex-wrap justify-center gap-6 md:gap-10 max-w-4xl">
          <button v-for="p in store.profiles.list" :key="p.id" type="button" :class="['group flex flex-col items-center gap-3 w-28 md:w-36', { 'pick-in': startPos.has(p.id) }]"
            :style="enter(p.id)" @click="picker.pick(p.id)">
            <span class="block rounded-full ring-2 ring-transparent group-hover:ring-brand group-focus:ring-brand transition-transform group-hover:scale-105">
              <span v-if="p.avatar" :class="[size, 'bg-brand text-white block rounded-full bg-cover bg-center flex-shrink-0']" :style="photoStyle(p.avatar)"></span>
              <span v-else :class="[size, 'bg-brand text-white rounded-full flex items-center justify-center font-bold flex-shrink-0']">{{ profileInitial(p) }}</span>
            </span>
            <span class="text-sm md:text-base font-semibold text-gray-300 group-hover:text-gray-100 truncate max-w-full">{{ p.name }}</span>
          </button>
          <button type="button" class="group flex flex-col items-center gap-3 w-28 md:w-36 pick-in" :style="delay(300 + startPos.size * 90)" @click="picker.edit(null)">
            <span :class="[size, 'rounded-full border-2 border-dashed border-gray-600 group-hover:border-brand text-gray-500 group-hover:text-brand flex items-center justify-center transition group-hover:scale-105']"><i class="fas fa-plus"></i></span>
            <span class="text-sm md:text-base font-semibold text-gray-400 group-hover:text-gray-100">{{ store.profiles.list.length ? t('Add profile') : t('Create profile') }}</span>
          </button>
        </div>
      </div>
      <AppFooter />
    </div>
  </Teleport>
</template>
